import React from "react";
import {
  TEMPLATE_CATEGORY,
  TEMPLATE_FILE_TYPE,
  TEMPLATE_STATUS,
} from "../constants/template";
import { FaFilePdf } from "react-icons/fa";
import {
  Card,
  CardHeader,
  CardTitle,
  Badge,
  CardCategory,
  RichTextContainer,
  WhatsAppFooter,
  WhatsAppButton,
} from "../styles";

export const TemplatePreview = ({
  template,
  selectedTemplate,
  setSelectedTemplate,
}) => {
  const { id, name, category, status, components } = template;

  /**
   * Obtiene el tipo de archivo a partir de la extensión en la URL.
   * @param {string} url - URL del archivo.
   * @returns {"image" | "video" | "pdf" | null}
   */
  const getFileTypeFromUrl = (url) => {
    if (!url || typeof url !== "string") return null;

    const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "svg"];
    const VIDEO_EXTENSIONS = ["mp4"];
    const PDF_EXTENSIONS = ["pdf"];

    // Extrae la parte del path sin query strings ni hash
    const cleanUrl = url.split("?")[0].split("#")[0];
    const ext = cleanUrl.split(".").pop()?.toLowerCase();

    if (!ext) return null;

    if (IMAGE_EXTENSIONS.includes(ext)) return TEMPLATE_FILE_TYPE.IMAGE;
    if (VIDEO_EXTENSIONS.includes(ext)) return TEMPLATE_FILE_TYPE.VIDEO;
    if (PDF_EXTENSIONS.includes(ext)) return TEMPLATE_FILE_TYPE.PDF;

    return null;
  };

  const getLabelStatus = (status) => {
    if (status === TEMPLATE_STATUS.APPROVED) return "Aprobado";

    if (status === TEMPLATE_STATUS.PENDING) return "Pendiente";

    if (status === TEMPLATE_STATUS.REJECTED) return "Rechazado";

    return "";
  };

  const getLabelCategory = (category) => {
    if (category === TEMPLATE_CATEGORY.MARKETING) return "Comercial";

    if (category === TEMPLATE_CATEGORY.GENERAL) return "Avisos generales";

    return "";
  };

  /**
   * Convierte el body del template a HTML con rich text:
   * - Variables {{n}} → chip verde (clase highlighted-variable)
   * - *texto* → <strong>texto</strong>
   * - _texto_ → <em>texto</em>
   * - ~texto~ → <del>texto</del>
   * - Saltos de línea → <br />
   *
   * @param {string} body       - Texto con placeholders {{n}} y markdown WhatsApp.
   * @param {string[]} variables - Nombres de las variables en orden.
   * @returns {string} HTML listo para dangerouslySetInnerHTML.
   */
  const renderBodyWithVariables = (body, variables = []) => {
    if (!body) return "";

    // 1. Escapar caracteres HTML básicos para evitar XSS con el texto plano
    const escape = (str) =>
      str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

    const escaped = escape(body);

    // 2. Reemplazar variables {{n}} por chips verdes
    const withVars = escaped.replace(/\{\{(\d+)\}\}/g, (match, number) => {
      const index = parseInt(number, 10) - 1;
      const label =
        index >= 0 && index < variables.length ? variables[index] : match;
      return `<span class="highlighted-variable">${escape(label)}</span>`;
    });

    // 3. Convertir markdown WhatsApp → HTML
    //    Orden: bold antes de italic para evitar conflictos con `_` dentro de `*`
    const withBold = withVars.replace(/\*(.*?)\*/g, "<strong>$1</strong>");
    const withItalic = withBold.replace(/_(.*?)_/g, "<em>$1</em>");
    const withStrike = withItalic.replace(/~(.*?)~/g, "<del>$1</del>");

    // 4. Saltos de línea
    const withBreaks = withStrike.replace(/\n/g, "<br />");

    return withBreaks;
  };

  const handleOnSelectTemplate = () => {
    setSelectedTemplate({
      ...template,
      components: {
        ...template.components,
        bodyFormatted: renderBodyWithVariables(
          components.body,
          components.variables,
        ),
        fileType: getFileTypeFromUrl(components.headerUrl),
      },
    });
  };

  return (
    <Card
      $selected={selectedTemplate?.id === id}
      onClick={handleOnSelectTemplate}
    >
      <CardHeader>
        <CardTitle>{name}</CardTitle>

        <Badge $status={status}>{getLabelStatus(status)}</Badge>
      </CardHeader>

      <CardCategory>{getLabelCategory(category)}</CardCategory>

      <div>
        {components.headerUrl &&
          (() => {
            const fileType = getFileTypeFromUrl(components.headerUrl);

            if (!fileType) return null;

            return (
              <div
                style={{
                  marginBottom: "1rem",
                  borderRadius: "8px",
                  overflow: "hidden",
                  objectFit: "scale-down",
                  aspectRatio: "3/2",
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: fileType === "pdf" ? "100px" : "auto",
                  backgroundColor: "#f0f0f0",
                }}
              >
                {/* Image */}
                {fileType === "image" && (
                  <img
                    src={components.headerUrl}
                    alt="Preview"
                    style={{ width: "100%", height: "auto", display: "block" }}
                  />
                )}

                {/* Video */}
                {fileType === "video" && (
                  <video
                    src={components.headerUrl}
                    controls
                    style={{ width: "100%", height: "auto", display: "block" }}
                  />
                )}

                {/* PDF */}
                {fileType === "pdf" && (
                  <div>
                    <FaFilePdf style={{ fontSize: "50px", color: "#e74c3c" }} />
                  </div>
                )}
              </div>
            );
          })()}

        <RichTextContainer
          style={{ width: "100%", borderRadius: "8px" }}
          dangerouslySetInnerHTML={{
            __html: renderBodyWithVariables(
              components.body,
              components.variables,
            ),
          }}
        />

        {components.footer && (
          <WhatsAppFooter style={{ width: "100%" }}>
            {components.footer}
          </WhatsAppFooter>
        )}

        {components.buttons.length > 0 && (
          <div
            style={{
              marginTop: "5px",
              backgroundColor: "white",
              borderRadius: "0 0 7.5px 7.5px",
              overflow: "hidden",
              width: "100%",
            }}
          >
            {components.buttons.map((btn, idx) => (
              <WhatsAppButton key={idx}>{btn.text}</WhatsAppButton>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};
